import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useFirePostOfficeTrigger } from '@atlassian/post-office-context/post-office-trigger';

import type { ShareContentType, ShareProgress } from '../share-criteria-types.ts';
import { evaluateShareCriteria } from '../utils/evaluateShareCriteria.ts';
import {
	hasContentTypeTriggeredBefore,
	markContentTypeTriggered,
} from '../utils/flagToShareTriggerState.ts';
import { getTriggerId } from '../utils/getTriggerId.ts';
import { isShareProgressForType } from '../utils/isShareProgressForType.ts';

import { useFlagToShareEnrollment } from './useFlagToShareEnrollment.ts';
import { useInactivityTimer } from './useInactivityTimer.ts';

type PendingProgress = {
	contentId: string;
	type: ShareContentType;
	progress: ShareProgress;
};

export type NotifyFlagToShareProgress = (progress: ShareProgress | null | undefined) => void;

export type UseFlagToShareParams = {
	type: ShareContentType;
	contentId: string;
};

export type UseFlagToShareResult = {
	shouldCollectProgress: boolean;
	notifyProgress: NotifyFlagToShareProgress;
};

/** Owns the lazy Flag-to-Share lifecycle for normalized progress from one active content. */
export function useFlagToShare({ type, contentId }: UseFlagToShareParams): UseFlagToShareResult {
	const { status: enrollmentStatus, checkEligibility } = useFlagToShareEnrollment({ type });
	// Invalidates progress callbacks when content or type changes.
	const contentSession = useMemo(() => ({ contentId, type }), [contentId, type]);
	// Resets trigger settlement only when content changes.
	const triggerContentSession = useMemo(() => ({ contentId }), [contentId]);
	// Rerenders collection state when this trigger session settles.
	const [triggeredContentSession, setTriggeredContentSession] = useState<object | null>(null);
	const hasTriggered = triggeredContentSession === triggerContentSession;
	const shouldCollectProgress =
		!hasTriggered && (enrollmentStatus === 'unchecked' || enrollmentStatus === 'eligible');
	const lifecycleSession = useMemo(
		() => ({ contentId, enrollmentStatus, type }),
		[contentId, enrollmentStatus, type],
	);
	// Tracks the committed session for stale callback rejection.
	const activeContentSessionRef = useRef<object | null>(null);
	// Blocks reentrant work before settled state commits.
	const triggeredContentSessionRef = useRef<object | null>(null);
	// Retains valid progress while enrollment is pending.
	const pendingProgressRef = useRef<PendingProgress | null>(null);

	useLayoutEffect(() => {
		activeContentSessionRef.current = contentSession;
		return () => {
			if (activeContentSessionRef.current === contentSession) {
				activeContentSessionRef.current = null;
			}
		};
	}, [contentSession]);

	// Local storage read is synchronous, so this can be read directly during
	// render — no loading state or deferred-fire handling needed.
	const hasTriggeredBefore = hasContentTypeTriggeredBefore(type);

	const { fireTrigger } = useFirePostOfficeTrigger({
		triggerId: getTriggerId(type, hasTriggeredBefore),
	});

	const handleInactivityElapsed = useCallback(() => {
		if (triggeredContentSessionRef.current === triggerContentSession) {
			return;
		}

		triggeredContentSessionRef.current = triggerContentSession;
		setTriggeredContentSession(triggerContentSession);
		fireTrigger({ isActive: true });

		if (!hasTriggeredBefore) {
			markContentTypeTriggered(type);
		}
	}, [fireTrigger, hasTriggeredBefore, triggerContentSession, type]);

	const { clear: clearTimer, restart: restartTimer } = useInactivityTimer({
		enabled: enrollmentStatus === 'eligible' && !hasTriggered,
		session: lifecycleSession,
		onElapsed: handleInactivityElapsed,
	});

	const processEligibleProgress = useCallback(
		(progress: ShareProgress) => {
			if (!evaluateShareCriteria(progress)) {
				clearTimer();
				return;
			}

			restartTimer();
		},
		[clearTimer, restartTimer],
	);

	useEffect(() => {
		const pendingProgress = pendingProgressRef.current;
		if (
			pendingProgress &&
			(pendingProgress.contentId !== contentId || pendingProgress.type !== type)
		) {
			pendingProgressRef.current = null;
			return;
		}

		if (enrollmentStatus === 'ineligible') {
			pendingProgressRef.current = null;
			return;
		}

		if (enrollmentStatus === 'eligible' && pendingProgress) {
			pendingProgressRef.current = null;
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- only the later inactivity timeout can settle state
			processEligibleProgress(pendingProgress.progress);
		}
	}, [contentId, enrollmentStatus, processEligibleProgress, type]);

	const notifyCurrentProgress = useCallback<NotifyFlagToShareProgress>(
		(progress) => {
			if (hasTriggered || triggeredContentSessionRef.current === triggerContentSession) {
				return;
			}

			if (enrollmentStatus === 'ineligible') {
				return;
			}

			if (progress === null) {
				pendingProgressRef.current = null;
				clearTimer();
				return;
			}

			if (!isShareProgressForType(type, progress)) {
				return;
			}

			if (enrollmentStatus === 'eligible') {
				processEligibleProgress(progress);
				return;
			}

			pendingProgressRef.current = { contentId, type, progress };
			if (enrollmentStatus === 'unchecked') {
				void checkEligibility();
			}
		},
		[
			checkEligibility,
			clearTimer,
			contentId,
			enrollmentStatus,
			hasTriggered,
			processEligibleProgress,
			triggerContentSession,
			type,
		],
	);
	const latestNotifyProgressRef = useRef(notifyCurrentProgress);

	useLayoutEffect(() => {
		latestNotifyProgressRef.current = notifyCurrentProgress;
	}, [notifyCurrentProgress]);

	const notifyProgress = useCallback<NotifyFlagToShareProgress>(
		(progress) => {
			if (activeContentSessionRef.current !== contentSession) {
				return;
			}

			latestNotifyProgressRef.current(progress);
		},
		[contentSession],
	);

	return {
		shouldCollectProgress,
		notifyProgress,
	};
}
